import {
    IBalusterGeometry,
    IRailingGeometry,
    IStairConfig,
    IStepGeometry,
    IStringerGeometry,
    ISupportPosts,
    IVec3,
} from '@stair-craft/common/util';

import { STRINGER_THICKNESS, SUPPORT_POST_GROOVE } from '../../constants';
import { railLineOffset } from '../shared-board-band/board-band';
import { IPlanPoint, clipByHalfPlane, planCentroid, planDot, planSub, planUnit } from '../shared-plan-geometry/plan-geometry';
import { winderRiserBoardOutline } from '../shared-riser-board/riser-board';
import { insideOutline, treadFootprint } from '../shared-tread-footprint/tread-footprint';

/** An outer corner this close to a wall is fixed to it: the gap is closed by the mounting (mm). */
const WALL_REACH: number = STRINGER_THICKNESS;
/** A hair of slack when deciding whether two squares overlap or a point lies on a post (mm). */
const SLACK: number = 0.5;

/** Whether two plan squares, given by centres and half sides, overlap by more than a hair. */
function squaresOverlap(a: IPlanPoint, aHalf: number, b: IPlanPoint, bHalf: number): boolean {
    const reach: number = aHalf + bHalf - SLACK;
    return Math.abs(a.x - b.x) < reach && Math.abs(a.z - b.z) < reach;
}

/**
 * The stretch of a segment, as parameters from 0 to 1, that lies over a plan square; null when it
 * misses the square. The segment is clipped against the square's four sides in turn.
 */
function overSquare(a: IVec3, b: IVec3, centre: IPlanPoint, half: number): [number, number] | null {
    let from: number = 0;
    let to: number = 1;
    const reach: number = half + SLACK;
    for (const [start, delta, middle] of [
        [a.x, b.x - a.x, centre.x],
        [a.z, b.z - a.z, centre.z],
    ]) {
        if (Math.abs(delta) < 1e-9) {
            if (Math.abs(start - middle) > reach) {
                return null;
            }
            continue;
        }
        const t1: number = (middle - reach - start) / delta;
        const t2: number = (middle + reach - start) / delta;
        from = Math.max(from, Math.min(t1, t2));
        to = Math.min(to, Math.max(t1, t2));
    }
    return from <= to ? [from, to] : null;
}

/**
 * The top of a support post: the highest handrail point over its square — the rail meets it there,
 * at a bend or on the straight between two — or the landing top where no rail passes over it.
 */
function postTop(centre: IPlanPoint, half: number, railings: readonly IRailingGeometry[], landingTop: number): number {
    let top: number = landingTop;
    for (const railing of railings) {
        const points: IVec3[] = railing.handrail.points;
        for (let i: number = 0; i < points.length - 1; i++) {
            const stretch: [number, number] | null = overSquare(points[i], points[i + 1], centre, half);
            if (stretch) {
                const rise: number = points[i + 1].y - points[i].y;
                top = Math.max(top, points[i].y + rise * stretch[0], points[i].y + rise * stretch[1]);
            }
        }
    }
    return top;
}

/**
 * Cuts a landing board block at a support post's face, leaving the groove's depth inside the post:
 * the end the post stands at is the one it is cut at.
 */
export function cutAtPost(stringer: IStringerGeometry, centre: IPlanPoint, half: number): void {
    const block: IStringerGeometry['block'] = stringer.block;
    if (!block) {
        return;
    }
    const xs: number[] = block.outline.map((point: IPlanPoint) => point.x);
    const zs: number[] = block.outline.map((point: IPlanPoint) => point.z);
    const apart: boolean =
        Math.min(...xs) >= centre.x + half - SLACK ||
        Math.max(...xs) <= centre.x - half + SLACK ||
        Math.min(...zs) >= centre.z + half - SLACK ||
        Math.max(...zs) <= centre.z - half + SLACK;
    if (apart) {
        return;
    }
    const along: IPlanPoint = planUnit(planSub(block.to, block.from));
    const postAt: number = planDot(planSub(centre, block.from), along);
    const middleAt: number = planDot(planSub(planCentroid(block.outline), block.from), along);
    const toward: number = postAt > middleAt ? -1 : 1;
    const face: number = postAt + toward * (half - SUPPORT_POST_GROOVE);
    const facePoint: IPlanPoint = { x: block.from.x + along.x * face, z: block.from.z + along.z * face };
    block.outline = clipByHalfPlane(block.outline, facePoint, { x: along.x * toward, z: along.z * toward });
}

/**
 * Cuts a winder's riser board square at the face of a post it runs into: the face across the
 * riser's way from the post centre to the board's middle.
 */
function cutRiserAtPost(step: IStepGeometry, centre: IPlanPoint, half: number): void {
    if (!step.isWinder || !step.winderCorners) {
        return;
    }
    const outline: IPlanPoint[] = winderRiserBoardOutline(step, step.winderCorners);
    const xs: number[] = outline.map((point: IPlanPoint) => point.x);
    const zs: number[] = outline.map((point: IPlanPoint) => point.z);
    const apart: boolean =
        Math.min(...xs) >= centre.x + half - SLACK ||
        Math.max(...xs) <= centre.x - half + SLACK ||
        Math.min(...zs) >= centre.z + half - SLACK ||
        Math.max(...zs) <= centre.z - half + SLACK;
    if (apart) {
        return;
    }
    const middle: IPlanPoint = planCentroid(outline);
    const alongX: boolean = Math.abs(middle.x - centre.x) >= Math.abs(middle.z - centre.z);
    const normal: IPlanPoint = alongX ? { x: Math.sign(middle.x - centre.x), z: 0 } : { x: 0, z: Math.sign(middle.z - centre.z) };
    const face: IPlanPoint = { x: centre.x + normal.x * half, z: centre.z + normal.z * half };
    step.riserOutline = clipByHalfPlane(outline, face, normal);
}

/**
 * The highest top of the turn treads a post carries: those whose footprint holds its centre or a
 * corner of its square. A winder post stands where several treads meet.
 */
function treadsTop(centre: IPlanPoint, half: number, turnSteps: readonly IStepGeometry[], config: IStairConfig): number {
    const probes: IPlanPoint[] = [
        centre,
        ...[-1, 1].flatMap((dx: number) => [-1, 1].map((dz: number) => ({ x: centre.x + dx * half, z: centre.z + dz * half }))),
    ];
    // No tread over the post is a miss of the placement; the first turn tread's top keeps it standing.
    return Math.max(
        turnSteps[0].position.y,
        ...turnSteps
            .filter((step: IStepGeometry) =>
                probes.some((probe: IPlanPoint) => insideOutline(treadFootprint(step, config.stringerType), probe))
            )
            .map((step: IStepGeometry) => step.position.y)
    );
}

/**
 * The highest board top over a post's square: a тетива meeting the post ends in it, so the post
 * reaches the board's top there and the tread ends housed in the board stay hidden in the post.
 */
function boardsTop(centre: IPlanPoint, half: number, stringers: readonly IStringerGeometry[]): number {
    let top: number = -Infinity;
    for (const stringer of stringers) {
        const block: IStringerGeometry['block'] = stringer.block;
        const line: IVec3[] = block
            ? [
                  { x: block.from.x, y: block.topFrom, z: block.from.z },
                  { x: block.to.x, y: block.topTo, z: block.to.z },
              ]
            : stringer.points;
        for (let i: number = 1; i < line.length; i++) {
            const stretch: [number, number] | null = overSquare(line[i - 1], line[i], centre, half);
            if (stretch) {
                const rise: number = line[i].y - line[i - 1].y;
                top = Math.max(top, line[i - 1].y + rise * stretch[0], line[i - 1].y + rise * stretch[1]);
            }
        }
    }
    return top;
}

/**
 * Whether a plan corner of the stair, in stair millimetres, stands at a room wall: the outer
 * boards there are fixed to the wall and carry the turn without a post.
 */
function atWall(corner: IPlanPoint, placement: IPlanPoint, config: IStairConfig): boolean {
    const x: number = placement.x + corner.x;
    const z: number = placement.z + corner.z;
    return x <= WALL_REACH || z <= WALL_REACH || x >= config.roomWidth - WALL_REACH || z >= config.roomLength - WALL_REACH;
}

/**
 * The outer corners of a winder turn, where the outer boards meet at the far side of the turn
 * square: one for an L, one past each flight for a U — in stair millimetres, on the stair's edge.
 */
function winderOuterCorners(config: IStairConfig, turnStartZ: number): IPlanPoint[] {
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    if (config.type === 'u-shape') {
        // A U winder turn reaches past the march width by half the gap between the flights.
        const far: number = turnStartZ + sw + config.flightGap / 2;
        const span: number = 2 * sw + config.flightGap;
        return isRight
            ? [
                  { x: 0, z: far },
                  { x: span, z: far },
              ]
            : [
                  { x: sw, z: far },
                  { x: sw - span, z: far },
              ];
    }
    return [{ x: isRight ? 0 : sw, z: turnStartZ + sw }];
}

/** A landing's four corners, inset by half a post and the rail edge; three for an L without a railing. */
function landingPostCentres(config: IStairConfig, landing: IStepGeometry, inset: number): IPlanPoint[] {
    const x0: number = landing.position.x;
    const z0: number = landing.position.z;
    const xs: number[] = [x0 + inset, x0 + landing.treadWidth - inset];
    const zs: number[] = [z0 + inset, z0 + landing.treadDepth - inset];
    const corners: IPlanPoint[] = xs.flatMap((x: number) => zs.map((z: number) => ({ x, z })));
    if (config.type !== 'l-shape' || config.railing.enabled) {
        return corners;
    }
    // Without a railing an L landing stands on three posts, where the flights' boards meet it: the
    // far corner, away from the first flight's end and the second flight's start, carries none.
    const farX: number = config.turnDirection === 'right' ? xs[0] : xs[1];
    return corners.filter((corner: IPlanPoint) => corner.x !== farX || corner.z !== zs[1]);
}

/** A winder turn's inner post: at the front of the turn square on the second flight's side for an L, between the flights for a U. */
function innerPostCentres(config: IStairConfig, turnSteps: readonly IStepGeometry[], turnStartZ: number, inset: number): IPlanPoint[] {
    const sw: number = config.stairWidth;
    const isRight: boolean = config.turnDirection === 'right';
    if (config.type === 'u-shape') {
        // The treads over the gap begin at the turn's inner front line, past the flights' own start.
        const lo: number = isRight ? sw : -config.flightGap;
        const hi: number = lo + config.flightGap;
        // Over a gap only the corners strictly inside it count: the flights' own treads end at its sides.
        const edge: number = config.flightGap > 2 * SLACK ? SLACK : -SLACK;
        const front: number[] = turnSteps
            .flatMap((step: IStepGeometry) => treadFootprint(step, config.stringerType))
            .filter((point: IPlanPoint) => point.x >= lo + edge && point.x <= hi - edge)
            .map((point: IPlanPoint) => point.z);
        return [{ x: (lo + hi) / 2, z: Math.min(turnStartZ + config.stairWidth, ...front) + inset }];
    }
    return [{ z: turnStartZ + inset, x: isRight ? sw - inset : inset }];
}

/** The middle of the stair across its flights, stair millimetres: an outer corner lies on one side of it. */
function innerX(config: IStairConfig): number {
    const sw: number = config.stairWidth;
    if (config.type === 'u-shape') {
        return config.turnDirection === 'right' ? sw + config.flightGap / 2 : -config.flightGap / 2;
    }
    return sw / 2;
}

/**
 * Where the posts of a turn stand: a landing's four corners, inset by half a post and the rail
 * edge; a winder turn's one post in the inner corner — at the front of the turn square on the
 * second flight's side for an L, between the flights at the front line for a U — and one under
 * every outer corner that stands off the walls.
 */
function postCentres(
    config: IStairConfig,
    turnSteps: readonly IStepGeometry[],
    turnStartZ: number,
    inset: number,
    placement: IPlanPoint
): IPlanPoint[] {
    const landing: IStepGeometry | undefined = turnSteps.find((step: IStepGeometry) => step.isLanding);
    if (landing) {
        return landingPostCentres(config, landing, inset);
    }
    const outer: IPlanPoint[] = winderOuterCorners(config, turnStartZ)
        .filter((corner: IPlanPoint) => !atWall(corner, placement, config))
        .map((corner: IPlanPoint) => ({
            // Inset toward the turn square's middle along both edges, as the inner post is.
            x: corner.x + (corner.x <= innerX(config) ? inset : -inset),
            z: corner.z - inset,
        }));
    return [...innerPostCentres(config, turnSteps, turnStartZ, inset), ...outer];
}

/**
 * Support posts of an L or U turn (SC-CALC-093), from the floor: at the landing corners, or one in
 * the inner corner of a winder turn and one under each of its outer corners that stands off the
 * walls (the stair's placement in the room tells which). A post on the railing line takes the place of the railing post
 * there and reaches the handrail; elsewhere it reaches the highest tread top it carries. The turn
 * boards are cut at the post faces into a groove, a landing riser keeps clear of the posts, and the
 * railing posts and balusters the support posts replace are dropped.
 */
export function buildTurnSupportPosts(
    config: IStairConfig,
    turnSteps: readonly IStepGeometry[],
    turnStartZ: number,
    railings: readonly IRailingGeometry[],
    stringers: readonly IStringerGeometry[],
    placement: IPlanPoint
): ISupportPosts | undefined {
    if (turnSteps.length === 0) {
        return undefined;
    }
    const width: number = config.railing.postWidth;
    const half: number = width / 2;
    const inset: number = railLineOffset(config.stringerType, config.railing);
    const stair: IRailingGeometry[] = railings.filter((railing: IRailingGeometry) => railing.kind !== 'upper');
    const centres: IPlanPoint[] = postCentres(config, turnSteps, turnStartZ, inset, placement);
    const posts: IBalusterGeometry[] = centres.map((centre: IPlanPoint) => ({
        position: { x: centre.x, y: 0, z: centre.z },
        height: postTop(
            centre,
            half,
            stair,
            // A kosour carries the treads on its top, so only a тетива's top rises over them.
            Math.max(
                treadsTop(centre, half, turnSteps, config),
                config.stringerType === 'tetiva' ? boardsTop(centre, half, stringers) : -Infinity
            )
        ),
        rotation: 0,
    }));
    for (const railing of stair) {
        const kept: (upright: IBalusterGeometry, uprightHalf: number) => boolean = (
            upright: IBalusterGeometry,
            uprightHalf: number
        ): boolean => !centres.some((centre: IPlanPoint) => squaresOverlap(upright.position, uprightHalf, centre, half));
        railing.posts = railing.posts.filter((post: IBalusterGeometry) => kept(post, railing.postWidth / 2));
        railing.balusters = railing.balusters.filter((baluster: IBalusterGeometry) => kept(baluster, railing.balusterWidth / 2));
    }
    for (const centre of centres) {
        for (const stringer of stringers) {
            cutAtPost(stringer, centre, half);
        }
        for (const step of turnSteps) {
            cutRiserAtPost(step, centre, half);
        }
    }
    const landing: IStepGeometry | undefined = turnSteps.find((step: IStepGeometry) => step.isLanding);
    if (landing) {
        // The landing riser keeps clear of the front posts, along the tread width from `position.x`.
        const span: { start: number; end: number } = landing.riserSpan ?? { start: 0, end: landing.treadWidth };
        landing.riserSpan = { start: Math.max(span.start, inset + half), end: Math.min(span.end, landing.treadWidth - inset - half) };
    }
    return { width, posts };
}
